import {
  Card,
  Group,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { ChevronDown, Clock3 } from 'lucide-react';
import { useMemo } from 'react';
import { useDescriptiveStatistics } from '../hooks/useDescriptiveStatistics';
import type {
  ChartViewId,
  CustomTimeSegment,
  SpectralWorkspaceSession,
} from '../types';
import {
  buildTrendEndOptions,
  buildTrendStartOptions,
} from '../utils/trendZoom';
import { CustomSegmentManager } from './CustomSegmentManager';
import { DescriptiveStatCard } from './DescriptiveStatCard';

type ZoomSelectsProps = {
  startOptions: Array<{ value: string; label: string }>;
  endOptions: Array<{ value: string; label: string }>;
  startValue: string | null;
  endValue: string | null;
  onStartChange: (value: string | null) => void;
  onEndChange: (value: string | null) => void;
  availableSeconds: number[];
};

function ZoomSelects({
  startOptions,
  endOptions,
  startValue,
  endValue,
  onStartChange,
  onEndChange,
  availableSeconds,
}: ZoomSelectsProps) {
  return (
    <Group gap='md' align='flex-end' wrap='wrap'>
      <Select
        label='Inizio'
        placeholder='Seleziona timestamp inizio'
        variant='filled'
        leftSection={<Clock3 size={14} />}
        rightSection={<ChevronDown size={14} />}
        data={startOptions}
        value={startValue}
        onChange={value => {
          if (!value) {
            onStartChange(null);
            return;
          }
          onStartChange(value);
          if (endValue && Number(value) >= Number(endValue)) {
            const nextSecond = availableSeconds.find(
              second => second > Number(value)
            );
            onEndChange(nextSecond != null ? nextSecond.toString() : null);
          }
        }}
        clearable
        w={220}
      />
      <Select
        label='Fine'
        placeholder='Seleziona timestamp fine'
        variant='filled'
        leftSection={<Clock3 size={14} />}
        rightSection={<ChevronDown size={14} />}
        data={endOptions}
        value={endValue}
        onChange={onEndChange}
        clearable
        w={220}
      />
    </Group>
  );
}

type StatisticsSectionProps = {
  readySessions: SpectralWorkspaceSession[];
  activeViewId: ChartViewId;
  onActiveViewChange: (viewId: ChartViewId) => void;
  customSegments: CustomTimeSegment[];
  onCustomSegmentsChange: (segments: CustomTimeSegment[]) => void;
  trendStart: string | null;
  trendEnd: string | null;
  onTrendStartChange: (value: string | null) => void;
  onTrendEndChange: (value: string | null) => void;
};

export function StatisticsSection({
  readySessions,
  activeViewId,
  onActiveViewChange,
  customSegments,
  onCustomSegmentsChange,
  trendStart,
  trendEnd,
  onTrendStartChange,
  onTrendEndChange,
}: StatisticsSectionProps) {
  const stats = useDescriptiveStatistics({
    readySessions,
    activeViewId,
    customSegments,
    trendStart,
    trendEnd,
  });

  const trendStartOptions = useMemo(
    () => buildTrendStartOptions(stats.availableSeconds),
    [stats.availableSeconds]
  );

  const trendEndOptions = useMemo(
    () => buildTrendEndOptions(stats.availableSeconds, trendStart),
    [stats.availableSeconds, trendStart]
  );

  if (readySessions.length === 0) {
    return (
      <Card withBorder>
        <Text c='dimmed' size='sm'>
          Aggiungi almeno una sessione con dati HR per visualizzare la
          validazione statistica.
        </Text>
      </Card>
    );
  }

  return (
    <Stack gap='md'>
      <Card withBorder>
        <Stack gap='md'>
          <div>
            <Title order={4}>Validazione statistica</Title>
            <Text size='sm' c='dimmed'>
              Questa sezione descrive la stabilità del gruppo nel segmento
              selezionato. Non mostra un test pre/post: qui si legge quanto i
              soggetti sono simili o variabili tra loro nel tratto scelto.
            </Text>
          </div>

          <CustomSegmentManager
            activeViewId={activeViewId}
            onActiveViewChange={onActiveViewChange}
            customSegments={customSegments}
            onCustomSegmentsChange={onCustomSegmentsChange}
            availableSeconds={stats.availableSeconds}
            maxAvailableSecond={stats.maxAvailableSecond}
          />

          {activeViewId === 'full' && (
            <ZoomSelects
              startOptions={trendStartOptions}
              endOptions={trendEndOptions}
              startValue={trendStart}
              endValue={trendEnd}
              onStartChange={onTrendStartChange}
              onEndChange={onTrendEndChange}
              availableSeconds={stats.availableSeconds}
            />
          )}

          {!stats.segmentBounds ? (
            <Text size='sm' c='dimmed'>
              Nessun dato disponibile per il tratto selezionato.
            </Text>
          ) : (
            <SimpleGrid cols={{ base: 1, sm: 3, lg: 5 }} spacing='md'>
              {stats.rows.map(row => (
                <DescriptiveStatCard
                  key={`descriptive-stat-${row.feature}`}
                  row={row}
                  segmentLabel={stats.segmentLabel}
                />
              ))}
            </SimpleGrid>
          )}
        </Stack>
      </Card>
    </Stack>
  );
}
